import { Container, Rectangle, RenderTexture, Sprite, type Renderer, type Texture } from 'pixi.js';
import type { CardDef } from '../../core/card';
import type { CardTitle } from '../../core/ids';
import type { CardArt, CardArtRegistry } from './art';
import { faceSize, hasCardAspect, paddedFaceSize, type FaceSize, type PaddedFaceSize } from './metrics';
import { PAD_FADE_RATIO, padFadeFilter } from './padFade';
import { getLang, t } from '../../i18n';
import { isTextureAlive, keepAlive } from '../textureLifetime';

/**
 * **牌面缓存** —— 通用层里"接受来自牌的 FBO 结果"的那一端。
 *
 * ## 为什么一定要缓存
 *
 * `old_repo` 在这件事上翻过车：它给**每一张牌**都开一张全屏大小的 RenderTexture，
 * 一局 56 张牌就是几十张全屏纹理，必然爆显存（`docs/architecture.md` §7.2）。
 *
 * ## 为什么不做成一张大图集
 *
 * 分析过，结论是**买不到东西**，所以不做：
 *
 * - 每张牌是一个 `PerspectiveMesh`，而 `MeshPipe.execute(mesh)` **一次画一个 mesh** ——
 *   每张牌本来就是一个独立 draw call。图集省不掉 draw call，只省纹理绑定，
 *   而绑定次数和 mesh 数一样，也省不掉
 * - 图集要**按尺寸各来一张**（手牌 / 出牌区 / 检视 / 濒死是四个尺寸），
 *   要么开好几张大纹理、要么退回固定尺寸再缩放，两条路都比现在差
 * - 技术上可行（`PlaneGeometry` 的 UV 是构造时写成 [0,1]，
 *   而 `applyProjectiveTransformationToPlane` 只重写**位置**缓冲，UV 之后不再动，
 *   所以手动把 UV 重映射到子矩形是能work的）—— 只是不值。
 *
 * 正确的切法是**按卡种缓存，不按卡实例**：
 * 4 张 `Exception` 的牌面一模一样，就没有理由画 4 遍。
 * 一张 2.5:3.5 的牌面在 140pt 高时也就 100×140，乘上 DPR 也不到 1 MB，
 * 56 张全画一遍也就十几 MB —— 而且实际上只有 9 种不同的牌面。
 *
 * ## 键里都有什么
 *
 * `牌名 × 尺寸 × 分辨率 × 内容版本`
 *
 * 前三个决定"画多大"，最后一个决定"内容变没变"。
 * 版本变了就**原地重画**进同一张纹理（尺寸没变的话），
 * 这样已经持有这张纹理的 `CardView` 不需要重新接线。
 */

interface CacheEntry {
  readonly texture: RenderTexture;
  /** 上次画进去时用的完整键。和当前键不同就重画 */
  key: string;
}

export interface CardFaceCacheOptions {
  /** 渲染分辨率。默认跟渲染器一致，高分屏才不会糊 */
  readonly resolution?: number;
  /**
   * 出血淡出的宽度比。默认 `PAD_FADE_RATIO`。
   *
   * ⚠️ 传 0 = **不淡出**（保留硬裁）。这不是"效果选项"，
   *    是给调试页做 A/B 对比用的 —— 评审要确认"淡出到底有没有在起作用"，
   *    而它只在牌外那圈出血里看得见，光看一张图看不出来。
   */
  readonly fadeRatio?: number;
}

export class CardFaceCache {
  readonly #entries = new Map<string, CacheEntry>();
  /** 真正烘过多少次。懒烘焙会中途卡一下，所以这个数要能在调试页看见 */
  #bakes = 0;
  /** 烘焙用的临时画布，**按尺寸复用**。分配一张 RenderTexture 是烘焙里最贵的一步 */
  readonly #scratch = new Map<string, RenderTexture>();
  readonly #renderer: Renderer;
  readonly #arts: CardArtRegistry;
  readonly #resolution: number;
  readonly #fadeRatio: number;

  constructor(renderer: Renderer, arts: CardArtRegistry, options: CardFaceCacheOptions = {}) {
    this.#renderer = renderer;
    this.#arts = arts;
    this.#resolution = options.resolution ?? renderer.resolution;
    this.#fadeRatio = options.fadeRatio ?? PAD_FADE_RATIO;
  }

  /**
   * 拿到一张牌的牌面纹理（没有就画一张）。
   *
   * ⚠️ 返回的纹理**归缓存所有**，调用方不许 destroy 它。
   */
  get(card: CardDef, size: FaceSize = faceSize()): Texture {
    if (!hasCardAspect(size)) {
      // 这不是"忍一忍"的错误：比例一旦不统一，后面所有定位代码都失去前提
      throw new Error(t('render.faceCache.badAspect', { w: size.width, h: size.height }));
    }

    const art = this.#arts.get(card.id);
    const slot = `${card.id}|${size.width}x${size.height}@${this.#resolution}`;
    // ⚠️ 语言进 key：牌面文字（分类字样等）是 t() 出来的，切语言必须重烘，
    //    否则已烘好的牌面会停在旧语言。
    const key = `${slot}|${art.revision(card)}|${getLang()}`;

    const existing = this.#entries.get(slot);
    // ⚠️ 已经在缓存的贴图**也可能死掉**（GC、别处 destroy）。死了就当没有，重烘一张。
    if (existing !== undefined && existing.key === key && isTextureAlive(existing.texture)) {
      return existing.texture;
    }
    const reuse = existing !== undefined && isTextureAlive(existing.texture) ? existing.texture : null;

    // ⚠️ 纹理比牌**大一圈**：破框伸到牌外的图形必须真的画出来。
    //    尺寸变了要重建 —— 出血宽度是尺寸的一部分
    const padded = paddedFaceSize(size);
    // ⚠️ `keepAlive`：牌面长期留在缓存里，GC 不许回收（否则牌面变纯黑）
    const texture = keepAlive(
      reuse ??
        RenderTexture.create({
          width: padded.width,
          height: padded.height,
          resolution: this.#resolution,
          antialias: true,
          label: `card-face:${card.local}`,
        }),
    );

    this.#bake(art, card, size, padded, texture);
    this.#bakes++;

    this.#entries.set(slot, { texture, key });
    return texture;
  }

  /**
   * 把一张牌面烘进纹理。
   *
   * ⚠️ 有出血时要**两趟**：
   *
   * ```
   *   ① 内容 → scratch（含出血，整体平移 pad）
   *   ② scratch → 成品，中间过一道淡出 filter
   * ```
   *
   * 为什么不能一趟：淡出是要**减少 alpha**，而画只能加不能减。
   * 所以只能把内容先烘出来，再整体按"到边缘的距离"衰减一次。
   *
   * 两趟都只在**烘焙时**跑一次，不是每帧 —— 跑完就只剩一张静态纹理。
   */
  #bake(
    art: CardArt,
    card: CardDef,
    size: FaceSize,
    padded: PaddedFaceSize,
    target: RenderTexture,
  ): void {
    const built = art.build({ card, width: size.width, height: size.height });

    if (padded.pad <= 0) {
      this.#renderer.render({ container: built, target, clear: true });
      built.destroy({ children: true });
      return;
    }

    // ⚠️ scratch **按尺寸复用**，不是每烘一张新建一张 ——
    //    分配 RenderTexture 是这里最贵的一步，而它的内容每次都被整块覆盖
    const scratchKey = `${String(padded.width)}x${String(padded.height)}@${String(this.#resolution)}`;
    let scratch = this.#scratch.get(scratchKey);
    if (scratch === undefined) {
      scratch = keepAlive(
        RenderTexture.create({
          width: padded.width,
          height: padded.height,
          resolution: this.#resolution,
          antialias: true,
          label: 'card-face-scratch',
        }),
      );
      this.#scratch.set(scratchKey, scratch);
    }

    // ⚠️ 平移的是**画法的输出**，不是画法本身 ——
    //    `faces/` 里写的一律是牌的 0..W 坐标，出血有多宽是通用层的事
    const holder = new Container();
    built.position.set(padded.pad, padded.pad);
    holder.addChild(built);
    this.#renderer.render({ container: holder, target: scratch, clear: true });
    holder.destroy({ children: true });

    // filter 是**共享的**，所以这里不 destroy 它
    const fade = padFadeFilter({
      width: padded.width,
      height: padded.height,
      fadePx: size.width * this.#fadeRatio,
    });
    const copy = new Sprite(scratch);
    copy.filters = [fade];
    // 显式给 filterArea：不给的话 filter 会按精灵的**实测边界**算，
    // 而这里要的恰好是"整块纹理"，包括还没画东西的透明边
    copy.filterArea = new Rectangle(0, 0, padded.width, padded.height);
    this.#renderer.render({ container: copy, target, clear: true });
    copy.filters = [];
    copy.destroy();
  }

  /**
   * **把一批牌面一次烘完。**
   *
   * ⚠️ 这是给**开局前**用的。烘焙本身很便宜（一张牌两趟渲染），
   * 但它是**懒**的：第一次用到某张牌时才画。玩到一半突然要画一张没见过的牌，
   * 那两趟渲染就落在某一帧上 —— 单次只有几毫秒，但它是**可避免的抖动**。
   *
   * 所以真正的调用点是"进入对局 / 切场景"这种本来就有加载画面的时刻，
   * 一次把这一局可能用到的牌和尺寸都烘掉。之后整局 `bakeCount` 不再增长。
   *
   * @param size 这一批要按哪个尺寸烘。游戏的几种牌位尺寸各调一次
   */
  prewarm(cards: readonly CardDef[], size: FaceSize = faceSize()): void {
    for (const card of cards) this.get(card, size);
  }

  /**
   * 累计烘了多少次。
   *
   * 调试页拿它证明两件事：预热之后不再增长；以及**复用是有效的**
   * （同一张牌摆十次只烘一次）。
   */
  get bakeCount(): number {
    return this.#bakes;
  }

  /** 丢掉某张牌（或全部）的缓存。牌面内容会变的牌改完状态后调它 */
  invalidate(title?: CardTitle): void {
    for (const [slot, entry] of [...this.#entries]) {
      if (title !== undefined && !slot.startsWith(`${title}|`)) continue;
      entry.texture.destroy(true);
      this.#entries.delete(slot);
    }
  }

  /** 已经画了多少种牌面 —— 冒烟测试/调试面板用它确认没有重复画 */
  get size(): number {
    return this.#entries.size;
  }

  destroy(): void {
    this.invalidate();
    for (const t of this.#scratch.values()) t.destroy(true);
    this.#scratch.clear();
  }
}
